import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { ISubscriptionActionResponse } from 'api/types/response';
import api from 'api';
import { ISubscriptionWellfunnel } from 'api/components/Subscriptions/types';

import { openModal } from 'store/modals/actions';
import { fetchDiscountSubscriptionSuccess, requestResetSubscription } from 'store/subscriptions/actions';
import { checkSubscriptionRestored } from 'store/subscriptions/helpers';

import { CANCEL_OFFER_SUCCESS_SCREEN_ID, DISCOUNT_APPLIED_ERROR, DISCOUNT_APPLIED_SUCCESS } from 'constants/analytics';
import { DISCOUNT_TYPES } from 'constants/subscriptions';

import { sendAnalyticDiscountAppliedResult, trackScreenLoadCancelOffer } from 'services/Analytics';

import { ModalName } from 'modals/types';

import { changeSubscriptionErrorModalData, getChangeSubscriptionSuccessModalData } from 'helpers/subscriptions';

import { CancelFlowOffers, IDiscountSubscription } from 'types/subscription';

import { selectAllSubscriptions, selectDiscountSubscription } from '../selectors';

import { pollGetSubscriptions } from './pollGetSubscriptions';
import { handleAuthError } from './utils';

/**
 *
 * @param isExtraOffer boolean
 *
 * There is no way to apply a discount to a canceled subscription.
 * The resetSubscription method calls the reset subscription endpoint,
 * which restores the subscription and applies the discount under the hood.
 */
export function* resetSubscription({
    payload: isExtraOffer,
}: ReturnType<typeof requestResetSubscription>): SagaIterator {
    try {
        const [subscription]: ISubscriptionWellfunnel[] = yield select(selectAllSubscriptions);
        const discountProduct: IDiscountSubscription = yield select(selectDiscountSubscription);

        yield put(openModal(ModalName.WithLoaderModal, { title: 'subscription.waitingModal.updatingPlan.title' }));

        const result: ISubscriptionActionResponse = yield call(api.subscriptions.resetSubscription, {
            product: discountProduct,
            discount_type: isExtraOffer ? DISCOUNT_TYPES.extra : DISCOUNT_TYPES.base,
            id: subscription.external_id,
            provider: subscription.payment_provider,
        });

        if (result.error) {
            throw Error(result.error);
        }

        yield put(fetchDiscountSubscriptionSuccess(null));

        const subscriptions: ISubscriptionWellfunnel[] = yield call(
            pollGetSubscriptions,
            checkSubscriptionRestored(subscription.external_id)
        );

        const { product, upcoming_product, subscription_period } = subscriptions.find(
            ({ external_id }) => external_id === subscription.external_id
        ) as ISubscriptionWellfunnel;

        yield call(trackScreenLoadCancelOffer, {
            screenId: CANCEL_OFFER_SUCCESS_SCREEN_ID,
            eventLabel: { subscription_id: subscription.external_id, offer: CancelFlowOffers.Discount },
        });

        yield put(
            openModal(
                ModalName.SuccessModal,
                getChangeSubscriptionSuccessModalData(upcoming_product ?? { ...product, subscription_period })
            )
        );

        yield call(sendAnalyticDiscountAppliedResult, DISCOUNT_APPLIED_SUCCESS, subscription.product.name);
    } catch (error) {
        const authErrorWasHandled = yield* handleAuthError(error);

        if (authErrorWasHandled) return;

        yield put(
            openModal(ModalName.RetryErrorModal, {
                ...changeSubscriptionErrorModalData,
                retryAction: () => requestResetSubscription(isExtraOffer),
            })
        );

        yield call(
            sendAnalyticDiscountAppliedResult,
            DISCOUNT_APPLIED_ERROR,
            'Subscription plan not updated due to a technical issue'
        );
    }
}
